import { PlusIcon } from "lucide-react";
import { useIntl } from "react-intl";

import { Button } from "../ui/button";

import { emptyDataMessages } from "./emptyData.intl";

import { ButtonVariant } from "@/app/app.interface";
import DocsEmpty from "@/assets/images/docsEmpty.png";
import { cn, formatToCapitalizedWords } from "@/utils/core/generalUtils";

type EmptyDataProps = {
  title?: string;
  subtitle?: string;
  btnAction?: () => void;
  btnLabel?: string;
  btnVariant?: ButtonVariant;
  icon?: string;
  hideButton?: boolean;
  small?: boolean;
  titleClassName?: string;
  subtitleClassName?: string;
  containerClassName?: string;
};

const EmptyData = (props: EmptyDataProps): React.JSX.Element => {
  const {
    title,
    subtitle,
    btnAction,
    btnLabel,
    btnVariant,
    icon,
    hideButton,
    small,
    titleClassName,
    subtitleClassName,
    containerClassName,
  } = props;
  const intl = useIntl();
  const computedTitle = ((): string => {
    if (!title || !title.trim()) {
      return intl.formatMessage(emptyDataMessages.noDataFound);
    }
    // Preserve caller-provided full empty-state text when it already starts with "No".
    if (/^\s*no\b/i.test(title)) {
      return title;
    }
    return formatToCapitalizedWords(`No ${title} Found`);
  })();

  return (
    <div
      className={cn(
        "flex flex-col place-items-center gap-4",
        small ? "md:my-4" : "md:my-12 lg:my-32",
        containerClassName,
      )}
    >
      <img src={DocsEmpty} className='mx-auto max-h-32' alt='empty ui' />
      <h2 className={cn("text-lg font-medium 3xl:text-2xl", titleClassName)}>
        {computedTitle}
      </h2>
      <p className={cn("text-md", subtitleClassName)}>{subtitle}</p>
      {btnAction && !hideButton && (
        <Button
          variant={`${btnVariant ? btnVariant : "default"}`}
          className='flex items-center justify-between'
          type='button'
          onClick={btnAction}
        >
          {icon ? "header btn icon" : <PlusIcon size={20} className='mr-2' />}
          {btnLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyData;
